import { memo, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import Select, {
  InputActionMeta,
  MenuListProps,
  MenuProps,
  StylesConfig,
} from "react-select";

import { useRtl } from "@ryde/redux";
import { IDropdownItem, ISelectListItem } from "@ryde/shared";

import { ClearIndicator } from "./filter-clear-indicator";
import { FilterDropdownOption } from "./filter-dropdown-option";
import { FilterMenuList } from "./filter-menu-list";
import { FilterMultiselectMenu } from "./filter-multiselect-menu";
import { FilterSelectControl } from "./filter-select-control";

interface IMemoSelectProps {
  value: IDropdownItem[] | ISelectListItem[];
  selectAll: () => void;
  isSelectedAll: boolean;
  filteredDropDownItems: IDropdownItem[] | ISelectListItem[];
  dropdownItems: IDropdownItem[] | ISelectListItem[];
  onItemsCheckChange: (items: IDropdownItem[]) => void;
  stylesComputed: StylesConfig<unknown, true>;
  input: string;
  onInputChange: (inputValue: string, action: InputActionMeta) => void;

  isSelectNoneOptionEnabled?: boolean;
  isSelectNoneValueActive?: boolean;
  toggleFilterSelectNoneValue?: () => void;
}

export const MemoSelect = memo(
  ({
    value,
    selectAll,
    isSelectedAll,
    filteredDropDownItems,
    dropdownItems,
    onItemsCheckChange,
    stylesComputed,
    input,
    onInputChange,
    isSelectNoneOptionEnabled,
    isSelectNoneValueActive,
    toggleFilterSelectNoneValue,
  }: IMemoSelectProps) => {
    const { t } = useTranslation();
    const { isRtl } = useRtl();
    const menuSlotProps = {
      selectAll,
      isSelectedAll,
      isSelectNoneOptionEnabled,
      isSelectNoneValueActive,
      toggleFilterSelectNoneValue,
      isRtl,
      filteredOptions: (filteredDropDownItems ??
        dropdownItems) as MenuListProps["options"],
    };

    const menuSlotPropsRef = useRef(menuSlotProps);
    menuSlotPropsRef.current = menuSlotProps;

    const selectComponents = useMemo(
      () => ({
        DropdownIndicator: null,
        IndicatorSeparator: null,
        Control: FilterSelectControl,
        Option: FilterDropdownOption,
        ClearIndicator: ClearIndicator,
        Menu: (menuProps: MenuProps) => {
          const slotProps = menuSlotPropsRef.current;

          return (
            <FilterMultiselectMenu
              selectAll={slotProps.selectAll}
              isSelectedAll={slotProps.isSelectedAll}
              isSelectNoneOptionEnabled={slotProps.isSelectNoneOptionEnabled}
              isSelectNoneValueActive={slotProps.isSelectNoneValueActive}
              toggleFilterSelectNoneValue={
                slotProps.toggleFilterSelectNoneValue
              }
              {...menuProps}
              children={menuProps.children}
              isRtl={slotProps.isRtl}
            />
          );
        },
        MenuList: (menuProps: MenuListProps) => (
          <FilterMenuList
            {...menuProps}
            filteredOptions={menuSlotPropsRef.current.filteredOptions}
          />
        ),
      }),
      [],
    );

    return (
      <Select
        classNamePrefix="FilterMultiSelect"
        value={value}
        isMulti
        // eslint-disable-next-line jsx-a11y-x/no-autofocus -- intentional: focus the field when opened by user action
        autoFocus
        backspaceRemovesValue={false}
        components={selectComponents}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={true}
        menuIsOpen
        options={filteredDropDownItems ?? dropdownItems}
        onChange={onItemsCheckChange as (newValue: unknown) => void}
        placeholder={`${t(`general.SEARCH`)}...`}
        styles={stylesComputed}
        tabSelectsValue={false}
        inputValue={input}
        onInputChange={onInputChange}
      />
    );
  },
  (prevProps, newProps) => {
    const isDropdownItemsEqual =
      prevProps.dropdownItems.length === newProps.dropdownItems.length;
    const isFilteredDropDownItemsEqual =
      prevProps.filteredDropDownItems.length ===
      newProps.filteredDropDownItems.length;
    const isSelectedNoneEqual =
      prevProps.isSelectNoneValueActive === newProps.isSelectNoneValueActive;

    const isValueEqual = prevProps.value.length === newProps.value.length;
    const isInputEqual = prevProps.input === newProps.input;
    const isSelectAllEqual = prevProps.isSelectedAll === newProps.isSelectedAll;

    return (
      isDropdownItemsEqual &&
      isFilteredDropDownItemsEqual &&
      isValueEqual &&
      isInputEqual &&
      isSelectedNoneEqual &&
      isSelectAllEqual
    );
  },
);
